import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPlanetTransits, buildTransitReading } from '@astro/vedastro'
import HoroscopeWheelBackground from './HoroscopeWheelBackground'
import ZodiacCard from './ZodiacCard'
import ZodiacGlyph from './ZodiacGlyph'
import { ZODIAC_SIGNS } from './zodiacData'
import './HoroscopeSection.css'

function fmtReadingDate(date) {
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

export default function HoroscopeSection() {
  const [selectedSign, setSelectedSign] = useState(null)
  const panelRef = useRef(null)

  // Live "today" sky from the VedAstro service, fetched on load. Transit
  // positions are absolute per date, so this single fetch powers all 12 cards
  // and every reading for the day.
  const todayStr = new Date().toISOString().slice(0, 10)
  const {
    data: transits,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['vedastroTransits', todayStr],
    queryFn: ({ signal }) => getPlanetTransits(todayStr, signal),
    staleTime: Infinity,
    retry: false,
  })

  // Per-sign readings derived once from the day's transits.
  const readings = useMemo(() => {
    if (!transits) return null
    return Object.fromEntries(ZODIAC_SIGNS.map((s) => [s.sign, buildTransitReading(s.sign, 'today', transits)]))
  }, [transits])

  const selected = ZODIAC_SIGNS.find((s) => s.sign === selectedSign)
  const reading = selected && readings ? readings[selected.sign] : null

  useEffect(() => {
    if (selectedSign) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [selectedSign])

  return (
    <section
      id="horoscope-section"
      className="relative min-h-screen w-full flex flex-col justify-between items-center px-4 py-24 lg:px-12 z-10 overflow-hidden bg-white"
    >
      <HoroscopeWheelBackground />

      <div className="text-center space-y-4 max-w-3xl animate-float relative z-10">
        <div className="flex items-center justify-center gap-3 text-[10px] md:text-xs font-semibold tracking-[0.4em] text-amber-600 uppercase">
          <span>✦</span>
          <span className="border-b border-amber-400/50 pb-1">Daily Rashifal Matrix</span>
          <span>✦</span>
        </div>
        <h1 className="font-royal text-2xl sm:text-4xl md:text-5xl lg:text-5xl font-normal tracking-[0.06em] text-gray-900 uppercase leading-[1.3] select-none">
          See Your{' '}
          <span className="bg-linear-to-r from-gray-700 via-gray-500 to-gray-700 bg-clip-text text-transparent font-semibold">
            Horoscope
          </span>
          <span className="horoscope-free-text font-bold tracking-widest pl-2 inline-block">FREE</span>
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 font-light max-w-xl mx-auto leading-relaxed tracking-wide">
          Align your fate with macro cosmic transits. Select your custom western sign or ancient Vedic Rashi to decrypt your instant
          blueprint.
        </p>
      </div>

      <div className="w-full max-w-6xl grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 md:gap-6 my-12 px-2 sm:px-6 relative z-10">
        {ZODIAC_SIGNS.map((sign) => (
          <ZodiacCard
            key={sign.sign}
            sign={sign}
            energy={readings?.[sign.sign]?.energy ?? null}
            loading={isLoading}
            active={selectedSign === sign.sign}
            onSelect={() => setSelectedSign((prev) => (prev === sign.sign ? null : sign.sign))}
          />
        ))}
      </div>

      {selected && (
        <div ref={panelRef} className="w-full max-w-3xl mb-12 relative z-10">
          <div className="bg-white/95 backdrop-blur rounded-3xl border border-amber-200/70 shadow-2xl shadow-amber-100/60 p-6 sm:p-9">
            <div className="flex items-center gap-4 pb-5 mb-5 border-b border-amber-100">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200/70 text-amber-500 flex items-center justify-center shrink-0">
                <ZodiacGlyph sign={selected.sign} className="w-7 h-7" />
              </div>
              <div className="min-w-0 text-left">
                <h3 className="font-royal text-xl sm:text-2xl text-gray-900 leading-tight">
                  {selected.rashi} <span className="text-amber-600">· {selected.sign}</span>
                </h3>
                <p className="text-[10px] uppercase tracking-[.25em] text-amber-600/80 font-semibold mt-1">
                  Today&apos;s Rashifal · {fmtReadingDate(new Date())}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSign(null)}
                aria-label="Close reading"
                className="ml-auto shrink-0 w-8 h-8 rounded-full border border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-600 transition-colors text-xs"
              >
                ✕
              </button>
            </div>

            {isLoading && (
              <div className="animate-pulse space-y-3" aria-hidden="true">
                <div className="h-3 rounded-full bg-amber-100/80 w-full" />
                <div className="h-3 rounded-full bg-amber-100/80 w-[92%]" />
                <div className="h-3 rounded-full bg-amber-100/80 w-[65%]" />
                <div className="grid grid-cols-3 gap-3 pt-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-16 rounded-2xl bg-amber-50" />
                  ))}
                </div>
              </div>
            )}

            {isError && (
              <div className="text-center py-6">
                <div className="text-2xl mb-2">🔭</div>
                <p className="text-sm text-gray-600 font-medium">
                  {error?.message || 'We couldn’t reach the horoscope service right now. Please try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="mt-4 text-[10px] font-bold uppercase tracking-widest px-6 py-2.5 rounded-full border border-amber-300 text-amber-600 hover:bg-amber-50 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {reading && (
              <div className="text-left">
                <p className="text-[13px] sm:text-sm leading-[1.9] text-gray-600 font-light">{reading.text}</p>
                <div className="grid grid-cols-3 gap-3 mt-6">
                  {[
                    { label: 'Lucky No.', value: reading.luckyNum },
                    { label: 'Lucky Colour', value: reading.luckyColor },
                    { label: 'Mood', value: reading.luckyMood },
                  ].map((it) => (
                    <div key={it.label} className="text-center rounded-2xl py-3.5 px-2 bg-amber-50/70 border border-amber-100">
                      <p className="text-[8px] sm:text-[9px] uppercase tracking-[.2em] text-gray-400 font-semibold">{it.label}</p>
                      <p className="text-sm sm:text-base font-semibold text-gray-800 mt-1.5">{it.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col items-center space-y-4 w-full relative z-10">
        <Link
          id="master-cta-btn"
          to="/horoscope"
          className="premium-shimmer relative group bg-goldPrimary hover:bg-goldAccent text-astroDark font-bold text-xs md:text-sm tracking-[0.2em] uppercase px-12 py-4 rounded-full shadow-xl shadow-amber-200/60 transition-all duration-300 hover:scale-105"
        >
          <span className="flex items-center gap-2">
            Explore Full Matrix <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
          </span>
        </Link>
        <div className="w-48 h-[1.5px] bg-linear-to-r from-transparent via-amber-400 to-transparent opacity-60 pt-1" />
      </div>
    </section>
  )
}
